import { ref } from "vue";

import { produce } from "immer";
import { defineStore } from "pinia";
import { v4 as uuidv4 } from "uuid";

import { TabType } from "@/models/borrowerPlatformTemplates";

import type {
  Block,
  BorrowerPlatformTemplate,
  Tab,
  WelcomePageTab,
} from "@/models/borrowerPlatformTemplates";

export const useBorrowerPlatformTemplateBuilderStore = defineStore(
  "borrowerPlatformTemplate",
  () => {
    const selectedTabId = ref<string | null>(null);
    const activeBorrowerPlatformTemplate = ref<BorrowerPlatformTemplate | null>(
      null
    );

    const hasActiveTemplate = () =>
      Boolean(activeBorrowerPlatformTemplate.value?.content);

    const findTabIndex = (tabId: string) => {
      const tabs = activeBorrowerPlatformTemplate.value?.content?.tabs;
      return tabs?.findIndex((tab) => tab.id === tabId) ?? -1;
    };

    const updateTemplateWithProduce = (
      updater: (draft: BorrowerPlatformTemplate) => void
    ) => {
      if (!activeBorrowerPlatformTemplate.value?.content) {
        return;
      }

      activeBorrowerPlatformTemplate.value = produce(
        activeBorrowerPlatformTemplate.value,
        updater
      );
    };

    const withTabAndBlock = (
      tabId: string,
      blockId: string,
      callback: (
        tabIndex: number,
        blockIndex: number,
        tab: Extract<Tab, { type: "custom" }>
      ) => void
    ) => {
      const tabIndex = findTabIndex(tabId);
      if (tabIndex === -1) {
        return;
      }

      updateTemplateWithProduce((draft) => {
        const tab = draft.content.tabs?.[tabIndex];
        if (!tab || tab.type !== "custom" || !tab.blocks) {
          return;
        }

        const blockIndex = tab.blocks.findIndex(
          (block) => block.id === blockId
        );
        if (blockIndex !== -1) {
          callback(tabIndex, blockIndex, tab);
        }
      });
    };

    const setActiveBorrowerPlatformTemplate = (
      template: BorrowerPlatformTemplate | null
    ) => {
      activeBorrowerPlatformTemplate.value = template;
    };

    const updateActiveBorrowerPlatformTemplate = (
      updates: Partial<BorrowerPlatformTemplate>
    ) => {
      if (activeBorrowerPlatformTemplate.value) {
        Object.assign(activeBorrowerPlatformTemplate.value, updates);
      }
    };

    const deleteBorrowerPlatformTab = (tabId: string) => {
      const tabIndex = findTabIndex(tabId);
      if (tabIndex === -1) {
        return;
      }

      // Prevent deletion of welcome_page tabs
      const tab =
        activeBorrowerPlatformTemplate.value?.content.tabs?.[tabIndex];
      if (tab?.type === TabType.WELCOME_PAGE) {
        console.warn("Welcome page tabs cannot be deleted");
        return;
      }

      updateTemplateWithProduce((draft) => {
        if (draft?.content?.tabs) {
          draft.content.tabs.splice(tabIndex, 1);
        }
      });

      if (selectedTabId.value === tabId) {
        const updatedTabs = activeBorrowerPlatformTemplate.value?.content?.tabs;
        if (!updatedTabs || updatedTabs.length === 0) {
          selectedTabId.value = null;
        } else {
          const newSelectedIndex =
            tabIndex < updatedTabs.length ? tabIndex : updatedTabs.length - 1;
          selectedTabId.value = updatedTabs[newSelectedIndex].id;
        }
      }
    };

    const updateBorrowerPlatformTab = (values: {
      tabId: string;
      updates: Record<string, unknown>;
    }) => {
      const tabIndex = findTabIndex(values.tabId);
      if (tabIndex === -1) {
        return;
      }

      updateTemplateWithProduce((draft) => {
        if (draft.content.tabs) {
          const currentTab = draft.content.tabs[tabIndex];
          draft.content.tabs[tabIndex] = {
            ...currentTab,
            ...values.updates,
          } as Tab;
        }
      });
    };

    const createBorrowerPlatformTab = (payload: Record<string, unknown>) => {
      if (!hasActiveTemplate()) {
        return;
      }

      const newTab = {
        id: uuidv4(),
        ...payload,
      };

      updateTemplateWithProduce((draft) => {
        if (!draft.content.tabs) {
          draft.content.tabs = [];
        }
        draft.content.tabs.push(newTab as Tab);
      });

      // Only auto-select the new tab if it's not a logout tab
      if ((newTab as Tab).type !== TabType.LOG_OUT) {
        selectedTabId.value = newTab.id;
      }
      return newTab;
    };

    const setSelectedTabId = (id: string | null) => {
      selectedTabId.value = id;
    };

    const addBlockToBorrowerPlatformTab = (tabId: string, block: Block) => {
      const tabIndex = findTabIndex(tabId);
      if (tabIndex === -1) {
        return;
      }

      updateTemplateWithProduce((draft) => {
        const tab = draft.content.tabs?.[tabIndex];
        if (!tab || tab.type !== TabType.CUSTOM) {
          return;
        }

        if (!tab.blocks) {
          tab.blocks = [];
        }
        tab.blocks.push(block);
      });
    };

    const updateBorrowerPlatformBlock = (values: {
      tabId: string;
      blockId: string;
      updates: Partial<Block>;
    }) => {
      withTabAndBlock(
        values.tabId,
        values.blockId,
        (tabIndex, blockIndex, tab) => {
          if (tab.blocks) {
            tab.blocks[blockIndex] = {
              ...tab.blocks[blockIndex],
              ...values.updates,
            } as Block;
          }
        }
      );
    };

    const deleteBlockFromBorrowerPlatformTab = (
      tabId: string,
      blockId: string
    ) => {
      withTabAndBlock(tabId, blockId, (tabIndex, blockIndex, tab) => {
        if (tab.blocks) {
          tab.blocks.splice(blockIndex, 1);
        }
      });
    };

    const reorderBlocksInBorrowerPlatformTab = (
      tabId: string,
      reorderedBlocks: Block[]
    ) => {
      const tabIndex = findTabIndex(tabId);
      if (tabIndex === -1) {
        return;
      }

      updateTemplateWithProduce((draft) => {
        const tab = draft.content.tabs?.[tabIndex];
        if (tab && tab.type === TabType.CUSTOM) {
          tab.blocks = reorderedBlocks;
        }
      });
    };

    const updateBorrowerPlatformTabs = (tabs: Tab[]) => {
      if (!hasActiveTemplate()) {
        return;
      }

      updateTemplateWithProduce((draft) => {
        if (draft.content.tabs) {
          draft.content.tabs = tabs;
        }
      });
    };

    const clearActiveBorrowerPlatformTemplate = () => {
      activeBorrowerPlatformTemplate.value = null;
      selectedTabId.value = null;
    };

    const getWelcomePageTab = (): WelcomePageTab | undefined => {
      const tabs = activeBorrowerPlatformTemplate.value?.content.tabs;
      const welcomeTab = tabs?.find((tab) => tab.type === TabType.WELCOME_PAGE);
      return welcomeTab?.type === TabType.WELCOME_PAGE ? welcomeTab : undefined;
    };

    const updateWelcomePageTab = (updates: Record<string, unknown>) => {
      const welcomeTab = getWelcomePageTab();
      if (!welcomeTab) {
        return;
      }

      updateBorrowerPlatformTab({
        tabId: welcomeTab.id,
        updates,
      });
    };

    const getFooterData = () => {
      return {
        is_footer_enabled:
          activeBorrowerPlatformTemplate.value?.content?.is_footer_enabled ??
          false,
        footer: activeBorrowerPlatformTemplate.value?.content?.footer ?? null,
      };
    };

    const updateFooterData = (updates: {
      is_footer_enabled?: boolean;
      footer?: string | null;
    }) => {
      if (!hasActiveTemplate()) {
        return;
      }

      updateTemplateWithProduce((draft) => {
        if (!draft.content) {
          return;
        }

        if (updates.is_footer_enabled !== undefined) {
          draft.content.is_footer_enabled = updates.is_footer_enabled;
        }
        if (updates.footer !== undefined) {
          draft.content.footer = updates.footer;
        }
      });
    };

    return {
      activeBorrowerPlatformTemplate,
      selectedTabId,
      setActiveBorrowerPlatformTemplate,
      updateActiveBorrowerPlatformTemplate,
      deleteBorrowerPlatformTab,
      updateBorrowerPlatformTab,
      updateBorrowerPlatformTabs,
      createBorrowerPlatformTab,
      addBlockToBorrowerPlatformTab,
      updateBorrowerPlatformBlock,
      deleteBlockFromBorrowerPlatformTab,
      reorderBlocksInBorrowerPlatformTab,
      setSelectedTabId,
      clearActiveBorrowerPlatformTemplate,
      getWelcomePageTab,
      updateWelcomePageTab,
      getFooterData,
      updateFooterData,
    };
  }
);
